import { z } from 'zod';
import { objectIdSchema } from './common.schema';
import { TEMPLATE_DECISION_CLASSES, templateScheduleSchema, templateSetKeySchema } from './templateSet.schema';
import { TEMPLATE_PLACE_ISSUES } from './arrangementTemplate.schema';
import { ARRANGEMENT_LIMITS } from './arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3] A DECISION — ONE PER (SET, CLASS, CODE), AND EVERY TEMPLATE USING THE CODE INHERITS IT.
 *
 *  `SC` is decided once. Stored per template, 182 templates would multiply a few dozen decisions into
 *  hundreds and nobody would finish. A code that matches exactly one active master by its own code is
 *  RESOLVED BY RULE (shown as such, and overridable); everything else waits for a person.
 *
 *  The classes, each one plain question:
 *    SOURCE         — a force source or an in-charge unit code: which org unit, or skip it.
 *    FORCE_COLUMN   — a booklet column: which post type or vehicle type, a one-off column, or skip it.
 *    POLICY         — a duty point's set of columns: which policy carries them (or create one, through
 *                     the policy builder — its own approval, never a side effect).
 *    IN_CHARGE_ROLE — a booklet in-charge role on one policy: which role, a one-off role, or drop it.
 *    TOTALS         — a printed total that differs from the rows read: the rows as read, the printed
 *                     figure (by correcting the rows), or hold the template back.
 *    SCHEDULE       — a section: which festival days, at what times.
 *    DUTY_POINT     — a duty point that cannot be built as read: type its place, or skip it.
 *  Round the clock is FLAGGED and never built — it is not a class, because there is nothing to decide.
 *
 *  ⚠️ NAMED COLUMNS PER CLASS, NEVER ONE BAG ([P6b]). And an in-charge role is `mappedRole`, never a
 *  "role key" — `commandRoleIsolation.test.ts` CLAIM 5 confines that vocabulary to the files that
 *  build command entries, and a decision is not one of them.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

const sourceChoice = z.discriminatedUnion('choice', [
  /** A division, or a unit beneath one — never the commissionerate, which no allocation can name ([C1c §8]). */
  z.object({ choice: z.literal('ORG_UNIT'), orgUnitId: objectIdSchema }),
  /** Force rows from this source are LEFT OUT of the order; an in-charge's unit is simply not named. */
  z.object({ choice: z.literal('SKIP') }),
]);

const forceColumnChoice = z.discriminatedUnion('choice', [
  z.object({ choice: z.literal('POST_TYPE'), postTypeId: objectIdSchema }),
  z.object({ choice: z.literal('VEHICLE_TYPE'), vehicleTypeId: objectIdSchema }),
  /** A one-off column ([D4.3]) — policy-independent, optionally restricted to posts that may fill it. */
  z.object({
    choice: z.literal('ONE_OFF'),
    label: z.string().trim().min(1).max(80),
    restrictToPostTypeIds: z.array(objectIdSchema).max(20),
  }),
  z.object({ choice: z.literal('SKIP') }),
]);

const policyChoice = z.discriminatedUnion('choice', [
  z.object({ choice: z.literal('POLICY'), policyId: objectIdSchema }),
]);

const inChargeRoleChoice = z.discriminatedUnion('choice', [
  /** A role on the policy's current version, by its stable identity. */
  z.object({ choice: z.literal('ROLE'), mappedRole: z.string().trim().min(1).max(40) }),
  /** A one-off role on each duty point, labelled with the booklet's words. */
  z.object({ choice: z.literal('ONE_OFF'), label: z.string().trim().min(1).max(80) }),
  /** No in-charge entry is created for this role. */
  z.object({ choice: z.literal('DROP') }),
]);

const totalsChoice = z.discriminatedUnion('choice', [
  z.object({ choice: z.literal('AS_READ') }),
  /**
   * The printed figure, reached by correcting the rows that differ — a table total cannot be
   * apportioned to duty points by a program. Each corrected column must sum to its printed total.
   */
  z.object({
    choice: z.literal('PRINTED'),
    rows: z
      .array(
        z.object({
          dutyPointKey: z.string().min(1),
          column: z.string().min(1),
          n: z.number().int().min(0).max(10_000),
        }),
      )
      .min(1)
      .max(500),
  }),
  z.object({ choice: z.literal('HOLD') }),
]);

const scheduleChoice = z.discriminatedUnion('choice', [
  z.object({ choice: z.literal('SCHEDULE'), schedule: templateScheduleSchema }),
]);

const dutyPointChoice = z.discriminatedUnion('choice', [
  /**
   * The arrangement's own place limit — `ARRANGEMENT_LIMITS.PLACE_NAME`, the number every model reads.
   * [PLACE-500] A place flagged `TOO_LONG` (over 200 when the booklet was imported) can now be kept as read.
   */
  z.object({ choice: z.literal('PLACE'), place: z.string().trim().min(1).max(ARRANGEMENT_LIMITS.PLACE_NAME) }),
  z.object({ choice: z.literal('SKIP') }),
]);

const code = z.string().trim().min(1).max(300);
const note = z.string().trim().max(500).nullish();

export const recordTemplateDecisionSchema = z.discriminatedUnion('class', [
  z.object({ class: z.literal('SOURCE'), code, decision: sourceChoice, note }),
  z.object({ class: z.literal('FORCE_COLUMN'), code, decision: forceColumnChoice, note }),
  z.object({ class: z.literal('POLICY'), code, decision: policyChoice, note }),
  z.object({ class: z.literal('IN_CHARGE_ROLE'), code, decision: inChargeRoleChoice, note }),
  z.object({ class: z.literal('TOTALS'), code, decision: totalsChoice, note }),
  z.object({ class: z.literal('SCHEDULE'), code, decision: scheduleChoice, note }),
  z.object({ class: z.literal('DUTY_POINT'), code, decision: dutyPointChoice, note }),
]);

/** Every class's choice, as one union — what a stored decision and a suggestion carry. */
export const templateChoiceSchema = z.union([
  sourceChoice,
  forceColumnChoice,
  policyChoice,
  inChargeRoleChoice,
  totalsChoice,
  scheduleChoice,
  dutyPointChoice,
]);

/** What each class shows beside its question. One shape per class. */
export const templateDecisionContextSchema = z.discriminatedUnion('class', [
  z.object({
    class: z.literal('SOURCE'),
    /** People the booklet asks this source for, across every template using it. */
    people: z.number().int().min(0),
    usedFor: z.enum(['FORCE', 'IN_CHARGE', 'BOTH']),
    /** The extraction's own reading — `mapped`, `probable`, `unmapped`. */
    bundleStatus: z.string().nullable(),
  }),
  z.object({ class: z.literal('FORCE_COLUMN'), people: z.number().int().min(0) }),
  z.object({
    class: z.literal('POLICY'),
    /** The columns this set needs, as post and vehicle type names. */
    columns: z.array(z.object({ kind: z.enum(['POST', 'VEHICLE']), id: objectIdSchema, name: z.string() })),
    dutyPoints: z.number().int().min(0),
  }),
  z.object({
    class: z.literal('IN_CHARGE_ROLE'),
    policyId: objectIdSchema,
    policyName: z.string(),
    label: z.string(),
    /** The roles on that policy's current version, to choose from. */
    roles: z.array(z.object({ id: z.string(), label: z.string() })),
  }),
  z.object({
    class: z.literal('TOTALS'),
    templateKey: z.string(),
    /** [TEMPLATE-FIND] The title the library names the template by. */
    templateTitle: z.string(),
    page: z.number().int().nullable(),
    columns: z.array(
      z.object({
        column: z.string(),
        printed: z.number().int().nullable(),
        parsed: z.number().int(),
        /** People in this column the booklet's in-charge cells account for. */
        inCharge: z.number().int().min(0),
        /**
         * Whether the printed figure can be reached by correcting rows: the table's duty points are
         * known and the column is a force column of its own. Otherwise only "as read" or "hold".
         */
        correctable: z.boolean(),
      }),
    ),
    dutyPoints: z.array(
      z.object({
        key: z.string(),
        sl: z.string().nullable(),
        place: z.string().nullable(),
        rows: z.array(z.object({ column: z.string(), n: z.number().int() })),
      }),
    ),
  }),
  z.object({
    class: z.literal('SCHEDULE'),
    sectionTitle: z.string(),
    sourceText: z.array(z.string()),
    pages: z.array(z.number().int()),
    roundTheClock: z.boolean(),
  }),
  z.object({
    class: z.literal('DUTY_POINT'),
    templateKey: z.string(),
    /** [TEMPLATE-FIND] The title the library names the template by. */
    templateTitle: z.string(),
    sl: z.string().nullable(),
    place: z.string().nullable(),
    /** [TEMPLATE-FIND] The booklet page the row is printed on; `null` where the extraction gave none. */
    page: z.number().int().nullable(),
    /**
     * [TEMPLATE-FIND] The row's cells PAMS could not read, as stored (`Inspr.: 34. Goranhata St. …`) —
     * often the place itself. `[]` when every cell was read.
     */
    unparsedCells: z.array(z.string()),
    /** [TEMPLATE-FIND] The other lines printed with the row, as stored (`When: …`). `[]` when none. */
    notes: z.array(z.string()),
    issue: z.enum([...TEMPLATE_PLACE_ISSUES, 'NO_FORCE']),
  }),
]);

export const templateDecisionRowSchema = z.object({
  class: z.enum(TEMPLATE_DECISION_CLASSES),
  code: z.string(),
  /**
   * What a person reads — the code itself, a column label, a section title. [TEMPLATE-FIND] A duty point
   * reads `t4 · (b)` where the booklet printed a serial, else `t152 · table 1, row 34`; never its raw key.
   */
  label: z.string(),
  /** How many templates use this code. */
  usedBy: z.number().int().min(0),
  /** A few of their keys, for the eye. */
  templateKeys: z.array(z.string()),
  context: templateDecisionContextSchema,
  suggestion: templateChoiceSchema.nullable(),
  decision: z
    .object({
      choice: templateChoiceSchema,
      /** Resolved by an exact code match, not by a person. */
      byRule: z.boolean(),
      decidedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
      decidedAt: z.iso.datetime().nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  /** A stored choice that no longer holds — an org unit deactivated, a policy archived. */
  problems: z.array(z.string()),
});

export const templateDecisionListSchema = z.object({
  items: z.array(templateDecisionRowSchema),
});

export const templateDecisionQuerySchema = z.object({
  class: z.enum(TEMPLATE_DECISION_CLASSES),
});

export const templateDecisionParamsSchema = z.object({ setKey: templateSetKeySchema });
